import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'
import Markdown from 'react-markdown'
// oxlint-disable-next-line no-restricted-imports -- kept to not break too many things in the config portal
import { Link } from 'react-router'

import { useConfigurationContext } from '../../context/configurationContext'
import { useComparableField } from '../../context/useComparableField'
import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import {
  type ConfigurationTemplateFunctionValue,
  isDeclaredConfigurationTemplateFunctionValue,
} from '../../types/templateModel'
import {
  getTemplateOperationAtDate,
  templateIdFromPath,
} from '../../uiModels/templateOperations/helpers'
import { isInherited } from '../../uiModels/templateOperations/types'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { DocumentationEntry } from '../documentationEntry/DocumentationEntry'
import { TemplateDocumentationEntry } from '../documentationEntry/TemplateDocumentationEntry'
import { EditableMarkdownViewer } from '../markdownEditor/EditableMarkdownViewer'
import { documentationFromTemplateField } from '../templateDocumentationEditors/helpers'
import { TemplateFieldDocumentationEditor } from '../templateDocumentationEditors/TemplateFieldDocumentationEditor'
import {
  functionNameWithModule,
  unnamedFunctionSignature,
} from '../templateFieldsTable/functionHelpers'

type Props = {
  data: ConfigurationTemplateFunctionValue
  areFieldsShownAsEditable: boolean
}

export const TemplateFunctionDocumentation = ({
  data,
  areFieldsShownAsEditable,
}: Props) => {
  const translate = useTranslate()
  const { viewAt } = useConfigurationContext()
  const { workspaceKey } = useOperationsContext()
  const { branch, server } = useNavigationParams()
  const templateIsLibrary = useTemplateIsLibrary()
  const type = data.type
  const signature = useMemo(
    () =>
      unnamedFunctionSignature(data.function?.fplusSource) ??
      (isDeclaredConfigurationTemplateFunctionValue(data) &&
        data.descriptor?.signature &&
        unnamedFunctionSignature(data.descriptor.signature)) ??
      '-',
    [data],
  )
  const { applicationTemplates: applicationTemplateOperations } =
    useApplicationViewOperationsContext()

  const name = useMemo(
    () =>
      data.function?.fplusSource
        ? functionNameWithModule(data.function.fplusSource)
        : '-',
    [data.function?.fplusSource],
  )

  const functionTypeTranslations = {
    declared: translate('templateViewer.functionType.declared'),
    dynamic: translate('templateViewer.functionType.dynamic'),
  }

  const isFieldInheritedFromParent = isInherited(data)

  const originTemplateId = data.origin
    ? templateIdFromPath(data.origin)
    : undefined

  // The inherited value should either be on the same layer as the current value or on a layer below.
  const originTemplate = originTemplateId
    ? getTemplateOperationAtDate(
        applicationTemplateOperations.get(originTemplateId),
        viewAt,
      )
    : null

  const comparableField =
    useComparableField<ConfigurationTemplateFunctionValue>(data.path)

  const valueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<ConfigurationTemplateFunctionValue>) => {
      if (!comparable) {
        return ''
      }

      const documentation = documentationFromTemplateField(comparable)

      return stringifyComparableValue(documentation?.value)
    },
    [],
  )

  return (
    <>
      <TemplateDocumentationEntry
        title={translate(
          'templateViewer.functionDetails.configurationFieldDocumentation',
        )}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={valueFromComparable}
            compareFilter="documentation"
          />
        }
        value={
          areFieldsShownAsEditable ? (
            <TemplateFieldDocumentationEditor field={data} />
          ) : (
            <EditableMarkdownViewer value={data.documentation?.value} />
          )
        }
        origin={data.documentation?.origin}
        path={`${data.path}.documentation`}
      />
      {data && isDeclaredConfigurationTemplateFunctionValue(data) && (
        <DocumentationEntry
          title={translate(
            'templateViewer.functionDetails.modelFieldTypeDocumentation',
          )}
          value={
            <div className="flex flex-col gap-y-4">
              <Markdown>{data.descriptor?.documentation}</Markdown>
            </div>
          }
        />
      )}
      <DocumentationEntry
        title={translate('templateViewer.functionDetails.type')}
        value={functionTypeTranslations[type]}
      />
      <DocumentationEntry
        title={translate('templateViewer.functionDetails.name')}
        value={name}
      />
      <DocumentationEntry
        title={translate('templateViewer.functionDetails.signature')}
        value={signature}
      />
      {isFieldInheritedFromParent && data.origin && (
        <DocumentationEntry
          title={translate('templateViewer.functionDetails.field.inheritance')}
          value={
            originTemplate?.layer ? (
              <Link
                to={configurationItem({
                  workspaceName: workspaceKey?.name,
                  layer: originTemplate.layer,
                  block: originTemplate.block,
                  server,
                  itemId: templateIdFromPath(data.origin),
                  branch,
                  type: templateIsLibrary(templateIdFromPath(data.origin))
                    ? 'library'
                    : 'template',
                })}
                className="rounded-md bg-inheritance p-2 text-amaze-inkBright hover:opacity-90"
              >
                {templateIdFromPath(data.origin)}
              </Link>
            ) : (
              templateIdFromPath(data.origin)
            )
          }
        />
      )}
    </>
  )
}
